import type { PropertyDescriptor, SourceContext, SourceStatus, SourceTypeDefinition } from '../core/definitions';
import { bool, num, str } from './base';
import { DeviceWatch, type AcquiredDevice } from './deviceWatch';
import { mapMediaError } from './errors';
import { StreamRuntime, stopTracks, watchEnded } from './streamRuntime';

export type AudioInputSettings = {
  deviceId: string;
  echoCancellation: boolean;
  noiseSuppression: boolean;
  autoGainControl: boolean;
  channelCount: number;
  /** Which input channel feeds the strip when the device delivers stereo. */
  channelSelect: 'both' | 'left' | 'right';
  /** Release the device while no visible item shows the source; reopens when shown again. */
  deactivateWhenHidden: boolean;
  /** Mixed into program in every scene, like an OBS global audio device (SRC-D-015). */
  alwaysOnProgram: boolean;
};

const DEFAULTS: AudioInputSettings = {
  deviceId: '',
  echoCancellation: false,
  noiseSuppression: false,
  autoGainControl: false,
  channelCount: 2,
  channelSelect: 'both',
  deactivateWhenHidden: false,
  alwaysOnProgram: false,
};

/** Edits arriving within this window become one reconciliation. */
export const UPDATE_COALESCE_MS = 150;

function read(s: Partial<AudioInputSettings>): AudioInputSettings {
  const select = str(s.channelSelect, DEFAULTS.channelSelect);
  return {
    deviceId: str(s.deviceId, DEFAULTS.deviceId),
    echoCancellation: bool(s.echoCancellation, DEFAULTS.echoCancellation),
    noiseSuppression: bool(s.noiseSuppression, DEFAULTS.noiseSuppression),
    autoGainControl: bool(s.autoGainControl, DEFAULTS.autoGainControl),
    channelCount: Math.round(num(s.channelCount, DEFAULTS.channelCount, 1, 2)),
    channelSelect: select === 'left' || select === 'right' ? select : 'both',
    deactivateWhenHidden: bool(s.deactivateWhenHidden, DEFAULTS.deactivateWhenHidden),
    alwaysOnProgram: bool(s.alwaysOnProgram, DEFAULTS.alwaysOnProgram),
  };
}

/** Processing flags can be changed on a running track without reopening the device (and without a gap). */
export function processingConstraints(c: AudioInputSettings): MediaTrackConstraints {
  return { echoCancellation: c.echoCancellation, noiseSuppression: c.noiseSuppression, autoGainControl: c.autoGainControl };
}

/** `sampleRate` is the engine context's rate, requested so the device is not resampled needlessly. */
export function audioInputConstraints(c: AudioInputSettings, sampleRate?: number): MediaStreamConstraints {
  const audio: MediaTrackConstraints = { ...processingConstraints(c), channelCount: { ideal: c.channelCount } };
  if (sampleRate && Number.isFinite(sampleRate)) audio.sampleRate = { ideal: sampleRate };
  if (c.deviceId) audio.deviceId = { exact: c.deviceId };
  return { audio, video: false };
}

const processingChanged = (a: AudioInputSettings, b: AudioInputSettings): boolean =>
  a.echoCancellation !== b.echoCancellation || a.noiseSuppression !== b.noiseSuppression || a.autoGainControl !== b.autoGainControl;
const hardChanged = (a: AudioInputSettings, b: AudioInputSettings): boolean => a.deviceId !== b.deviceId || a.channelCount !== b.channelCount;

class AudioInputRuntime extends StreamRuntime<AudioInputSettings> {
  private cfg: AudioInputSettings;
  private applied: AudioInputSettings;
  private stream: MediaStream | null = null;
  private acquiredDevice: AcquiredDevice | null = null;
  private inFlight = false;
  private updateTimer: ReturnType<typeof setTimeout> | null = null;
  private showing = true;
  private acquisitions = 0;
  private releasedByHide = false;
  private unwatch: (() => void) | null = null;
  private source: AudioNode | null = null;
  private splitter: ChannelSplitterNode | null = null;
  private merger: ChannelMergerNode | null = null;
  private readonly deviceWatch: DeviceWatch;

  constructor(ctx: SourceContext, settings: Partial<AudioInputSettings>) {
    super(ctx);
    this.cfg = read(settings);
    this.applied = this.cfg;
    this.deviceWatch = new DeviceWatch({
      kind: 'audioinput',
      configuredId: () => this.cfg.deviceId,
      acquired: () => this.acquiredDevice,
      status: () => {
        const s = this.getStatus();
        return { state: s.state, errorCode: s.error?.code };
      },
      reacquire: () => void this.acquire(),
    });
    if (this.cfg.deactivateWhenHidden) {
      queueMicrotask(() => {
        // setShowing() may already have opened (or deliberately not opened) the device.
        if (this.acquisitions === 0 && !this.destroyed && !this.releasedByHide) void this.acquire();
      });
    }
    else void this.acquire();
  }

  setShowing(showing: boolean): void {
    if (this.destroyed || showing === this.showing) return;
    this.showing = showing;
    this.applyVisibility();
  }

  private hidden(): boolean {
    return this.cfg.deactivateWhenHidden && !this.showing;
  }

  private applyVisibility(): void {
    if (this.hidden()) {
      if (!this.releasedByHide) this.releaseForHide();
    } else if (this.releasedByHide) {
      this.releasedByHide = false;
      void this.acquire();
    }
  }

  private releaseForHide(): void {
    this.releasedByHide = true;
    this.beginEpoch();
    if (this.updateTimer !== null) clearTimeout(this.updateTimer);
    this.updateTimer = null;
    this.inFlight = false;
    this.releaseStream();
    this.acquiredDevice = null;
    this.setStatus('idle', undefined, 'paused');
  }

  update(settings: AudioInputSettings): void {
    if (this.destroyed) return;
    this.cfg = read(settings);
    this.applyVisibility();
    if (this.updateTimer !== null) clearTimeout(this.updateTimer);
    this.updateTimer = setTimeout(() => {
      this.updateTimer = null;
      void this.reconcile();
    }, UPDATE_COALESCE_MS);
  }

  invoke(action: string): void {
    if (action === 'retry' && !this.destroyed) void this.acquire();
  }

  getReactionTrack(): MediaStreamTrack | null {
    return this.getStatus().state === 'live' ? this.stream?.getAudioTracks()[0] ?? null : null;
  }

  getCapabilities(): MediaTrackCapabilities | null {
    return this.stream?.getAudioTracks()[0]?.getCapabilities?.() ?? null;
  }

  getLiveInfo(): Record<string, string> {
    const track = this.stream?.getAudioTracks()[0];
    if (!track) return {};
    const s = track.getSettings?.() ?? {};
    const info: Record<string, string> = {};
    if (track.label) info.device = track.label;
    if (s.channelCount) info.channels = s.channelCount === 1 ? 'Mono' : `${s.channelCount} channels`;
    if (s.sampleRate) info.sampleRate = `${s.sampleRate / 1000} kHz`;
    const flag = (name: string, v: boolean | undefined) => `${name} ${v ? 'on' : 'off'}`;
    info.processing = [flag('echo cancellation', s.echoCancellation), flag('noise suppression', s.noiseSuppression), flag('auto gain', s.autoGainControl)].join(', ');
    return info;
  }

  private async reconcile(): Promise<void> {
    if (this.destroyed || this.inFlight) return;
    const next = this.cfg;
    const prev = this.applied;
    if (hardChanged(prev, next)) {
      void this.acquire();
      return;
    }
    if (prev.channelSelect !== next.channelSelect) {
      this.applied = { ...this.applied, channelSelect: next.channelSelect };
      this.shapeChannels();
    }
    if (!processingChanged(this.applied, next)) return;
    const track = this.stream?.getAudioTracks().find((t) => t.readyState === 'live');
    if (!track) {
      this.applied = next;
      return;
    }
    try {
      await track.applyConstraints(processingConstraints(next));
      this.applied = next;
      this.emit();
    } catch {
      void this.acquire();
    }
  }

  /** Routes one input channel to both outputs when the device delivers stereo and a side is selected. */
  private shapeChannels(): void {
    this.splitter?.disconnect();
    this.merger?.disconnect();
    this.splitter = null;
    this.merger = null;
    const source = this.source;
    if (!source) return;
    const delivered = this.stream?.getAudioTracks()[0]?.getSettings?.().channelCount ?? 2;
    const side = this.cfg.channelSelect;
    if (side === 'both' || delivered < 2) {
      this.setAudio(source);
      return;
    }
    const audioContext = this.ctx.audioContext;
    const splitter = audioContext.createChannelSplitter(2);
    const merger = audioContext.createChannelMerger(2);
    const index = side === 'left' ? 0 : 1;
    source.connect(splitter);
    splitter.connect(merger, index, 0);
    splitter.connect(merger, index, 1);
    this.splitter = splitter;
    this.merger = merger;
    this.setAudio(merger);
  }

  private releaseStream(): void {
    this.stopWatching();
    this.unwatch?.();
    this.unwatch = null;
    this.splitter?.disconnect();
    this.merger?.disconnect();
    this.splitter = null;
    this.merger = null;
    this.source = null;
    this.clearOutput();
    stopTracks(this.stream);
    this.stream = null;
  }

  private async acquire(): Promise<void> {
    const epoch = this.beginEpoch();
    this.acquisitions++;
    if (this.updateTimer !== null) clearTimeout(this.updateTimer);
    this.updateTimer = null;
    this.releaseStream();
    this.acquiredDevice = null;
    if (this.hidden()) {
      this.releasedByHide = true;
      this.applied = this.cfg;
      this.setStatus('idle', undefined, 'paused');
      return;
    }
    if (!navigator.mediaDevices?.getUserMedia) {
      this.fail('unsupported', 'getUserMedia is unavailable (requires a secure context)');
      return;
    }
    this.inFlight = true;
    const snapshot = this.cfg;
    this.applied = snapshot;
    this.setStatus('starting');
    try {
      let stream: MediaStream;
      try {
        stream = await navigator.mediaDevices.getUserMedia(audioInputConstraints(snapshot, this.ctx.audioContext.sampleRate));
      } catch (err) {
        if (!this.isCurrent(epoch)) return;
        const mapped = mapMediaError(err);
        this.fail(mapped.code, mapped.message);
        return;
      }
      if (!this.isCurrent(epoch)) {
        stopTracks(stream);
        return;
      }
      this.stream = stream;
      const s = stream.getAudioTracks()[0]?.getSettings?.() ?? {};
      this.acquiredDevice = { deviceId: s.deviceId || snapshot.deviceId, groupId: s.groupId ?? '' };
      this.unwatch = watchEnded(stream.getAudioTracks(), () => {
        if (this.isCurrent(epoch)) this.markEnded('waiting-for-device');
      });
      try {
        if (!(await this.present(stream, epoch, { video: false, audio: true }))) return;
      } catch (err) {
        if (!this.isCurrent(epoch)) return;
        this.fail('unknown', err instanceof Error ? err.message : String(err));
        return;
      }
      this.source = this.getAudio();
      this.shapeChannels();
      this.setStatus('live');
      this.watchLiveness(stream.getAudioTracks(), {
        code: 'unknown',
        message: 'The microphone is not delivering audio. It may be muted by the system or in use by another application.',
      });
    } finally {
      if (this.isCurrent(epoch)) {
        this.inFlight = false;
        if (hardChanged(this.applied, this.cfg) || processingChanged(this.applied, this.cfg) || this.applied.channelSelect !== this.cfg.channelSelect) {
          void this.reconcile();
        }
      }
    }
  }

  protected release(): void {
    if (this.updateTimer !== null) clearTimeout(this.updateTimer);
    this.updateTimer = null;
    this.deviceWatch.dispose();
    this.releaseStream();
  }
}

export const audioInputDefinition: SourceTypeDefinition<AudioInputSettings> = {
  kind: 'audio-input',
  displayName: 'Audio Input Capture',
  caps: { video: false, audio: true, retry: true },
  category: 'capture',
  duplicate: 'reference',
  defaults: () => ({ ...DEFAULTS }),
  properties(_settings: AudioInputSettings, status?: SourceStatus): PropertyDescriptor[] {
    const props: PropertyDescriptor[] = [
      { key: 'deviceId', label: 'Device', type: 'device', deviceKind: 'audioinput', description: 'Default follows the system default device.' },
      { key: 'channelCount', label: 'Channels', type: 'select', options: [{ value: 1, label: 'Mono' }, { value: 2, label: 'Stereo' }] },
      {
        key: 'channelSelect',
        label: 'Input channel',
        type: 'select',
        description: 'For a stereo device that carries one microphone on one side.',
        options: [
          { value: 'both', label: 'Both channels' },
          { value: 'left', label: 'Left only' },
          { value: 'right', label: 'Right only' },
        ],
      },
      { key: 'echoCancellation', label: 'Echo cancellation', type: 'boolean', description: 'Off by default: browser processing degrades production audio.' },
      { key: 'noiseSuppression', label: 'Noise suppression', type: 'boolean' },
      { key: 'autoGainControl', label: 'Automatic gain control', type: 'boolean' },
      {
        key: 'deactivateWhenHidden',
        label: 'Release the microphone while it is not showing',
        type: 'boolean',
        description: 'Closes the device when no visible item uses this source (it is then silent in program) and opens it again when shown.',
      },
      {
        key: 'alwaysOnProgram',
        label: 'Always on program',
        type: 'boolean',
        description: 'Keeps this microphone in the program mix in every scene, even scenes without it (like a global audio device in OBS). It also keeps the device open.',
      },
      { key: 'liveDevice', label: 'Device in use', type: 'info', liveKey: 'device' },
      { key: 'liveChannels', label: 'Delivering', type: 'info', liveKey: 'channels' },
      { key: 'liveRate', label: 'Sample rate', type: 'info', liveKey: 'sampleRate' },
      { key: 'liveProcessing', label: 'Processing applied', type: 'info', liveKey: 'processing' },
    ];
    if (!status || status.state === 'error' || status.state === 'ended') {
      props.push({ key: 'retry', label: 'Reconnect', type: 'button', action: 'retry' });
    }
    return props;
  },
  create: (ctx, settings) => new AudioInputRuntime(ctx, settings),
};
